// T009 — Paginated wallet transactions hook.
//
// Pages through `GET /admin/wallets/{account_id}?cursor=` — the same endpoint
// that returned the first page. Never `/wallet/transactions`: that one reads
// the caller's own wallet from the JWT, so an admin would get their own history.
import { useCallback, useEffect, useState } from "react";

import { adminGetWallet } from "../services/wallet/adminWalletApi";
import type { WalletTransaction } from "../types/wallet";

interface UseAdminWalletTransactionsResult {
  items: WalletTransaction[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

const PAGE_SIZE = 50;

const useAdminWalletTransactions = (
  accountId: string | undefined,
  // Required, and must be referentially stable (e.g. state): the reset effect below
  // depends on them, so a fresh `[]` default would reset on every render.
  initialItems: WalletTransaction[],
  initialCursor: string | null,
): UseAdminWalletTransactionsResult => {
  const [items, setItems] = useState<WalletTransaction[]>(initialItems);
  const [cursor, setCursor] = useState<string | null>(initialCursor);
  const [loading, setLoading] = useState(false);

  // A refresh of the first page (after issue/revoke) starts paging over.
  useEffect(() => {
    setItems(initialItems);
    setCursor(initialCursor);
  }, [initialItems, initialCursor]);

  const loadMore = useCallback(async () => {
    if (!accountId || loading || !cursor) return;
    setLoading(true);
    try {
      const page = await adminGetWallet(accountId, { limit: PAGE_SIZE, cursor });
      setItems((prev) => [...prev, ...(page.transactions ?? [])]);
      setCursor(page.next_cursor ?? null);
    } catch (err) {
      // Keep the cursor so "Load more" can be retried.
      console.error("Failed to load more wallet transactions", err);
    } finally {
      setLoading(false);
    }
  }, [accountId, cursor, loading]);

  return { items, loading, hasMore: cursor !== null, loadMore };
};

export default useAdminWalletTransactions;
